import React, { useEffect, useMemo, useRef, useState } from 'react';
import classNames from 'classnames';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import DraggableModal from 'components/organisms/draggableModal';
import BaseTabs from 'components/atoms/base-tabs';
import TemplateFormItem from 'components/molecules/base-form-template/form-item';
import BaseButton from 'components/atoms/baseButton';
import useUser from 'utils/useUser';
import FadeAnimation from 'components/atoms/base-animations/fade';
import { useRpmPatientDetails } from 'infrastructure/hooks/rpm';
import BaseInput from 'components/atoms/base-input';

import s from './styles.module.scss';
import { MAscriptsLibrary } from '../templates';
import Item from './item';

import type { BlurbContext, IScriptTemplate } from '../types';

interface IProps {
  patientGuid: string;
  opened: boolean;
  onClose: () => void;
  onSubmit: (template: string) => void;
}

const ScriptLibraryModal: React.FC<IProps> = (props) => {
  const { patientGuid, onClose, onSubmit, opened = false } = props;

  const { t } = useTranslation();

  const {
    query: { patient },
  } = useRpmPatientDetails({
    patientGuid,
  });

  const { fullName } = useUser();
  const rightRef = useRef<HTMLDivElement | null>(null);

  const [searchText, setSearchText] = useState<string>('');
  const [previewText, setPreviewText] = useState<string | null>(null);
  const [selectedTemplate, setSelectedTemplate] = useState<
    IScriptTemplate | undefined
  >(undefined);
  const [selectedTemplates, setSelectedTemplates] = useState<IScriptTemplate[]>(
    [],
  );

  const templates = useMemo(() => {
    return MAscriptsLibrary({
      userFullname: fullName,
      patientFullname: `${patient?.profile?.firstName} ${patient?.profile?.lastName}`,
      clinicName: `${patient?.clinic?.legalname}`,
    });
  }, [MAscriptsLibrary, fullName, patient?.profile.guid]);

  const filteredTemplates = useMemo(() => {
    if (!searchText || !searchText.trim()) {
      return templates;
    }
    return templates.filter((el) => el.name.toLowerCase().includes(searchText));
  }, [searchText, templates]);

  const formik = useFormik<any>({
    initialValues: {},
    onSubmit: (values) => {
      const blurbContext: BlurbContext = {
        includedSections: new Set(),
      };
      const blurbs: string[] = [];
      selectedTemplates.forEach((el, i, arr) => {
        const isLastBlurb = i + 1 === arr.length;
        if (!isLastBlurb) {
          blurbContext.includedSections.add('closingAdditionalQuestions');
          blurbContext.includedSections.add('closingUnderstanding');
        } else {
          blurbContext.includedSections.delete('closingAdditionalQuestions');
          blurbContext.includedSections.delete('closingUnderstanding');
        }
        blurbs.push(el.createBlurb(values, blurbContext));
      });
      onSubmit(blurbs.join(' '));
      onClose();
    },
    validateOnChange: false,
  });

  const handleChangeTab = (index: number) => {
    const item = selectedTemplates.find((_, i) => index === i);
    if (item) {
      setSelectedTemplate(item);
    }
  };

  const handleReorder = (from: number, to: number) => {
    setSelectedTemplates((prev) => {
      const next = [...prev];
      const [moved] = next.splice(from, 1);
      next.splice(to, 0, moved);
      return next;
    });
  };

  const handleAdd = (tempt: IScriptTemplate) => {
    if (!selectedTemplates.some((el) => el.name === tempt.name)) {
      setSelectedTemplates((prev) => [...prev, tempt]);
    }
    setSelectedTemplate(tempt);
  };

  const handleCloseTab = (index: number) => {
    setSelectedTemplates((prev) => {
      const next = prev.filter((_, i) => i !== index);

      const isClosingActive = prev[index]?.name === selectedTemplate?.name;

      if (isClosingActive) {
        const nextActive = next[index] ?? next[index - 1];

        setSelectedTemplate(nextActive);
      }

      if (!next.length) {
        formik.resetForm();
      }

      return next;
    });
  };

  const handlePreview = () => {
    const blurbContext: BlurbContext = {
      includedSections: new Set(),
    };
    const blurbs: string[] = [];
    selectedTemplates.forEach((el, i, arr) => {
      const isLastBlurb = i + 1 === arr.length;
      if (!isLastBlurb) {
        blurbContext.includedSections.add('closingAdditionalQuestions');
        blurbContext.includedSections.add('closingUnderstanding');
      } else {
        blurbContext.includedSections.delete('closingAdditionalQuestions');
        blurbContext.includedSections.delete('closingUnderstanding');
      }
      blurbs.push(el.createBlurb(formik.values, blurbContext));
    });
    setPreviewText(blurbs.join(' '));
  };

  const handleHidePreview = () => {
    setPreviewText(null);
  };

  const modalFooter = (
    <div className={s.footer}>
      {previewText ? (
        <BaseButton
          label={t('controls.hidePreview')}
          onClick={handleHidePreview}
          type="secondary"
        />
      ) : (
        formik.dirty && (
          <BaseButton
            label={t('controls.previewGeneratedNote')}
            onClick={handlePreview}
            type="secondary"
          />
        )
      )}
      <BaseButton
        label={t('controls.save')}
        onClick={formik.submitForm}
        width={130}
      />
    </div>
  );

  useEffect(() => {
    if (rightRef.current) {
      rightRef.current.scroll({ top: 0 });
    }
  }, [selectedTemplate?.name]);

  return (
    <DraggableModal
      modalClassName={s.modal}
      bodyClassName={s['modal-body']}
      title="ScriptLibraryModal"
      isOpen={opened}
      toggle={onClose}
      footer={modalFooter}
    >
      <FadeAnimation uniqueKey={`${previewText}`}>
        {previewText ? (
          <div className={s.preview}>{previewText}</div>
        ) : (
          <div className={s.body}>
            <div className={s.left}>
              <div className={s.search}>
                <BaseInput
                  placeholder={t('labels.search')}
                  onChange={setSearchText}
                />
              </div>
              <div className={s.list}>
                {filteredTemplates.map((el) => (
                  <Item
                    data={el}
                    selected={selectedTemplate?.name === el.name}
                    showAdd={!selectedTemplates.some((i) => i.name === el.name)}
                    onClick={setSelectedTemplate}
                    onAdd={handleAdd}
                  />
                ))}
              </div>
            </div>
            <div ref={rightRef} className={s.right}>
              <div>
                <div className={s.tabs}>
                  <BaseTabs
                    wrapperClassName={s['tabs-wrapper']}
                    activeKey={selectedTemplate?.name}
                    tabs={selectedTemplates.map((el) => ({
                      key: el.name,
                      label: el.name,
                      closable: true,
                      flat: false,
                    }))}
                    onChange={(_, index) => handleChangeTab(index)}
                    onCloseTab={(_, index) => handleCloseTab(index)}
                    onReorder={handleReorder}
                    ignoreSingle
                    draggable
                  />
                </div>
                {selectedTemplate && (
                  <div className={s.content} key={selectedTemplate.name}>
                    {selectedTemplate.template.map((formItem) => {
                      const err = false;
                      return (
                        <div
                          className={classNames(s['template-item'], {
                            [s.error]: err,
                          })}
                          key={formItem.key}
                        >
                          <TemplateFormItem
                            item={formItem}
                            formik={formik}
                            onChange={() => {
                              handleAdd(selectedTemplate);
                            }}
                          />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </FadeAnimation>
    </DraggableModal>
  );
};

export default ScriptLibraryModal;
